import {
  Company,
  CompanyExtras,
  CompanyFile,
  FileStatus,
  LEVERAGEABLE_ASSETS_SELECT_OPTIONS,
  REQUIRED_INSIGHTS_SELECT_OPTIONS,
  Router,
  SYNDICATION_INVESTOR_INVESTMENT_RANGE_OPTIONS,
  SYNDICATION_INVESTOR_INVESTMENT_TYPE_OPTIONS,
} from '@hum/types';
import { createQuery } from '../../utils';
import { parseNAICS } from './helpers';
import { getLabelForSelectValue } from '@hum/legacy-ui';
import { Session } from '@hum/api/src/Session/Session.model';
export const COMPANIES_EXTRA_PATH = '/extra';

export const getCompanyExtras = createQuery<CompanyExtras>(
  ({ api, router }) => {
    return api
      .get(`/v2/companies/${router.query.company_id}${COMPANIES_EXTRA_PATH}`)
      .then((data) => ({
        ...data,
        primaryNAICS: parseNAICS(data.naicsCode1, data.naicsName1),
        secondaryNAICS: parseNAICS(data.naicsCode2, data.naicsName2),
      }));
  }
);

const getCompanyId = (
  params?: { uuid?: string },
  router?: Router,
  session?: Session
) => {
  return (
    router?.query.company_id || params?.uuid || session?.user?.organizationId
  );
};

// Extract the common response handling
const handleCompanyResponse = (res: any): Company => ({
  ...res,
  tags: res.tags.map((tag) => ({
    value: tag.tagName,
    label: tag.tagName,
  })),
  acceptedToS: res.acceptedTos,
  acceptedToSOn: res.acceptedTosOn,
  acceptedFeeToSOn: res.acceptedFeeTosOn,
  acceptedNDA: res.acceptedNda,
  acceptedNDAOn: res.acceptedNdaOn,
  application: {
    ...res.capitalApplication,
    totalEquityRaised: res.capitalApplication.equityRaised,
    industryVertical: res.capitalApplication.verticalIndustry,
    accountTypeAnalytics: res.capitalApplication.wantsCompanyAnalytics,
    accountTypeSyndication: res.capitalApplication.wantsNonDilutiveFunding,
    requireMutualNda: res.capitalApplication.investorMutualNdaRequired,
    isTestAccount: res.capitalApplication.test,
    lastRaised:
      `${new Date(res.capitalApplication.lastRaised)
        .toISOString()
        .slice(5, 7)}${new Date(res.capitalApplication.lastRaised)
        .toISOString()
        .slice(0, 4)}` || null,
    investmentRanges: res.capitalApplication.investmentRanges?.map(
      ({ name }) => ({
        label: getLabelForSelectValue(
          SYNDICATION_INVESTOR_INVESTMENT_RANGE_OPTIONS,
          name
        ),
        value: name,
      })
    ),
    investmentTypes: res.capitalApplication.investmentTypes?.map(
      ({ name }) => ({
        label: getLabelForSelectValue(
          SYNDICATION_INVESTOR_INVESTMENT_TYPE_OPTIONS,
          name
        ),
        value: name,
      })
    ),
    leverageableAssets: res.capitalApplication.leverageableAssets?.map(
      ({ name }) => ({
        label: getLabelForSelectValue(LEVERAGEABLE_ASSETS_SELECT_OPTIONS, name),
        value: name,
      })
    ),
    requiredInsights: res.capitalApplication.requiredInsights?.map(
      ({ name }) => ({
        label: getLabelForSelectValue(REQUIRED_INSIGHTS_SELECT_OPTIONS, name),
        value: name,
      })
    ),
    otherRequiredInsights: res.capitalApplication.otherRequiredInsights?.map(
      ({ name }) => ({
        label: name,
        value: name,
      })
    ),
    unwantedInvestors: res.capitalApplication.unwantedInvestors?.map(
      ({ name }) => ({
        label: name,
        value: name,
      })
    ),
  },
});

export const getCompany = createQuery<Company, { uuid?: string }>(
  ({ api, params, router, session }) => {
    const companyId = getCompanyId(params, router, session);
    return api.get(`/v2/companies/${companyId}`).then(handleCompanyResponse);
  }
);

export const getSessionCompany = createQuery<Company>(({ api, session }) => {
  const companyId = session.user?.organizationId;
  return api.get(`/v2/companies/${companyId}`).then(handleCompanyResponse);
});

export const getCompanyFiles = createQuery<
  CompanyFile[],
  { companyId?: string }
>(({ api, params, session, router }) => {
  const isAdmin = session.user?.isAdmin;
  const companyId = isAdmin
    ? router.query.company_id
    : params?.companyId || session.user?.organizationId;

  if (!companyId) {
    return Promise.reject(new Error('Company ID is required'));
  }

  const baseUrl = '/v2/companies';
  const url = `${baseUrl}/${companyId}/files`;

  return api
    .get(url)
    .then((res) => (Array.isArray(res) ? res : []))
    .then((files) => files.filter((file) => file.status !== FileStatus.Failed));
});

export const getCompanyFile = createQuery<
  CompanyFile,
  { companyId?: number; fileId?: number }
>(({ api, params, session, router }) => {
  const isAdmin = session.user?.isAdmin;
  const companyId = isAdmin
    ? router.query.company_id
    : params?.companyId || session.user?.organizationId;
  const fileId = params?.fileId;

  if (!companyId) {
    return Promise.reject(new Error('Company ID is required'));
  }

  const baseUrl = '/v2/companies';
  const url = `${baseUrl}/${companyId}/files/${fileId}`;

  return api.get(url).then((res) => res || {});
});
